import BaseCustomerExperienceStoreType from '../shared-location/base-customer-experience-store-type';
import {
	RealTimeEvents,
	TrackingConfig,
	UpdateWayPointLocation,
	WayPointEtaUpdate,
	WayPointLocationUpdate
} from '../../types/shared-location.types';
import { getEnv } from 'mobx-easy';
import { RootEnv } from '../../helpers/create-store';
import { computed, observable, action, makeObservable } from 'mobx';
import { isBoolean as _isBoolean, isNil as _isNil, isEmpty as _isEmpty, get as _get } from 'lodash';
import EtaService from '../../services/eta-service';
import * as dayjs from 'dayjs';
import { TaskNote } from '@bringg/types';
class TrackingStore extends BaseCustomerExperienceStoreType<TrackingConfig> {
	protected realtimeWayPointLocation: google.maps.LatLngLiteral = null;

	protected realtimeWayPointEta: string = null;

	protected realTimeWayPointArrived: boolean = null;

	protected realTimeWayPointDone: boolean = null;

	protected realtimeShareCustomerNotes: TaskNote[] = null;

	constructor() {
		super();

		makeObservable<
			TrackingStore,
			| 'realtimeWayPointLocation'
			| 'realtimeWayPointEta'
			| 'realTimeWayPointArrived'
			| 'realTimeWayPointDone'
			| 'realtimeShareCustomerNotes'
		>(this, {
			realtimeWayPointLocation: observable,
			realtimeWayPointEta: observable,
			realTimeWayPointArrived: observable,
			realTimeWayPointDone: observable,
			realtimeShareCustomerNotes: observable,
			setRealtimeWayPointLocation: action,
			setRealtimeWayPointEta: action,
			setRealTimeWayPointArrived: action,
			setRealTimeWayPointDone: action,
			setRealTimeShareCustomerNotes: action,
			taskLocation: computed,
			wayPointArrived: computed,
			wayPointDone: computed,
			eta: computed,
			shareCustomerNotes: computed
		});
	}

	setRealtimeWayPointLocation(realtimeWayPointLocation: google.maps.LatLngLiteral) {
		this.realtimeWayPointLocation = realtimeWayPointLocation;
	}

	setRealtimeWayPointEta(realtimeWayPointEta: string) {
		this.realtimeWayPointEta = realtimeWayPointEta;
	}

	setRealTimeWayPointArrived(realTimeWayPointArrived: boolean) {
		this.realTimeWayPointArrived = realTimeWayPointArrived;
	}

	setRealTimeWayPointDone(realTimeWayPointDone: boolean) {
		this.realTimeWayPointDone = realTimeWayPointDone;
	}

	setRealTimeShareCustomerNotes(realtimeShareCustomerNotes: TaskNote[]) {
		this.realtimeShareCustomerNotes = realtimeShareCustomerNotes;
	}

	get taskLocation(): google.maps.LatLngLiteral {
		if (this.realtimeWayPointLocation) {
			return this.realtimeWayPointLocation;
		}

		return {
			lat: this.customerExperience.destination_lat,
			lng: this.customerExperience.destination_lng
		};
	}

	get wayPointArrived(): boolean {
		return _isBoolean(this.realTimeWayPointArrived)
			? this.realTimeWayPointArrived
			: this.customerExperience.arrived;
	}

	get wayPointDone(): boolean {
		return _isBoolean(this.realTimeWayPointDone) ? this.realTimeWayPointDone : this.customerExperience.done;
	}

	get eta(): string {
		if (!_isNil(this.realtimeWayPointEta)) {
			return this.normalizeEta(this.realtimeWayPointEta);
		}

		if (!_isNil(this.customerExperience.eta)) {
			return this.normalizeEta(this.customerExperience.eta);
		}

		return null;
	}

	get shareCustomerNotes() {
		return this.realtimeShareCustomerNotes || this.customerExperience.share_to_customer_notes;
	}

	shareUserLocation = async () => {
		try {
			const { userLocationService, sharedLocationService } = getEnv<RootEnv>();
			const position = await userLocationService.getCurrentLocation();

			const { task_id, find_me_token, uuid } = this.customerExperience;

			const updateWayPointLocation: UpdateWayPointLocation = {
				position,
				taskId: task_id,
				sharedLocationUuid: uuid,
				findMeToken: find_me_token
			};

			await sharedLocationService.updateWayPointLocation(updateWayPointLocation);
		} catch (e) {
			console.error('Failed to share my location', e);
		}
	};

	onWayPointLocationUpdate = ({ lat, lng }: WayPointLocationUpdate) => {
		this.setRealtimeWayPointLocation({ lat: parseFloat(lat), lng: parseFloat(lng) });
	};

	onWayPointEtaUpdate = ({ eta }: WayPointEtaUpdate) => {
		this.setRealtimeWayPointEta(eta);
	};

	onWayPointArrived = () => this.setRealTimeWayPointArrived(true);

	onWayPointDone = data => {
		this.setRealTimeWayPointDone(true);
		const shareCustomerNotes = _get(data, 'way_point.share_to_customer_notes');
		this.setRealTimeShareCustomerNotes(shareCustomerNotes);
	};

	private _startRealtimeTracking() {
		const { uuid, order_uuid, way_point_id, isUserAuthenticated } = this.customerExperience;
		const { realTimeService } = getEnv<RootEnv>();

		realTimeService.send(RealTimeEvents.WatchWayPoint, { order_uuid, way_point_id });
		realTimeService.send(RealTimeEvents.WatchOrder, {
			order_uuid,
			way_point_id,
			share_uuid: uuid,
			is_user_authenticated: isUserAuthenticated
		});
	}

	private _subscribeRealTimeEvents() {
		const { realTimeService } = getEnv<RootEnv>();

		realTimeService.listen(RealTimeEvents.WayPointDone, this.onWayPointDone);
		realTimeService.listen(RealTimeEvents.OrderCancelled, this.onWayPointDone);
		realTimeService.listen(RealTimeEvents.WayPointArrived, this.onWayPointArrived);
		realTimeService.listen(RealTimeEvents.WayPointEtaUpdate, this.onWayPointEtaUpdate);
		realTimeService.listen(RealTimeEvents.WayPointLocationUpdate, this.onWayPointLocationUpdate);
	}

	private normalizeEta = (eta: string): string => {
		const { set_min_eta_by_time_window, no_earlier_than } = this.customerExperience;

		if (set_min_eta_by_time_window && dayjs(eta).isBefore(dayjs(no_earlier_than))) {
			eta = dayjs(no_earlier_than).toISOString();
		}

		return EtaService.isEtaPassed(eta) ? dayjs().toISOString() : eta;
	};

	initialize() {
		this._startRealtimeTracking();
		this._subscribeRealTimeEvents();
	}

	dispose() {}
}

export default TrackingStore;
